if (!customElements.get('dl-bf-sales-products-m')) {
    class DlBfSalesProductsM extends HTMLElement {
        constructor() {
            super();
            this.type = this.dataset.dlType;
            
            // Event handlers storage for cleanup
            this.eventHandlers = new Map();
            this.resizeTimeout = null;
            this.isInitialized = false;
        }

        connectedCallback() {
            if (this.isInitialized) return;
            
            try {
                // Initialize Read More functionality (both types may need it)
                this.initReadMore();

                if (this.type === 'main_product' || this.type === 'product_group' || this.type === 'main_product_group') {
                    this.initMainProduct();
                } else if (this.type === 'accessories') {
                    this.initAccessories();
                }

                // Handle responsive behavior on window resize
                this.handleResize = this.debounce(() => {
                    this.checkReadMoreButtonVisibility();
                }, 250);
                
                window.addEventListener('resize', this.handleResize);
                this.isInitialized = true;
            } catch (error) {
                console.error('Error initializing dl-bf-sales-products-m:', error);
            }
        }

        disconnectedCallback() {
            // Clean up event listeners
            this.cleanup();
        }

        cleanup() {
            // Remove window resize listener
            if (this.handleResize) {
                window.removeEventListener('resize', this.handleResize);
            }

            // Clear resize timeout
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }

            // Remove all stored event handlers
            this.eventHandlers.forEach((handler, element) => {
                const [event, listener] = handler;
                element.removeEventListener(event, listener);
            });
            this.eventHandlers.clear();
        }

        debounce(func, wait) {
            return (...args) => {
                clearTimeout(this.resizeTimeout);
                this.resizeTimeout = setTimeout(() => func.apply(this, args), wait);
            };
        }

        // Update price display for a product item using pre-formatted prices from Liquid
        updatePriceDisplay(item, colorOption) {
            const priceContainer = item.querySelector('.dl-bf-sales-products-m__price');
            if (!priceContainer) return;

            const salePriceElement = priceContainer.querySelector('.dl-bf-sales-products-m__price-sale');
            const originalPriceElement = priceContainer.querySelector('.dl-bf-sales-products-m__price-original');

            if (salePriceElement && originalPriceElement) {
                // Use pre-formatted prices from Liquid
                const finalPriceFormatted = colorOption.dataset.dlVariantFinalPriceFormatted;
                const originalPriceFormatted = colorOption.dataset.dlVariantPriceFormatted;

                if (finalPriceFormatted && originalPriceFormatted) {
                    salePriceElement.textContent = finalPriceFormatted;
                    originalPriceElement.textContent = originalPriceFormatted;
                }
            }
        }

        // Update coupon code display when variant changes
        updateDiscountDisplay(item, colorOption) {
            const discountContainer = item.querySelector('.dl-bf-sales-products-m__discount');
            if (!discountContainer) return;

            const discountValue = colorOption.dataset.dlVariantDiscountDisplay;
            const discountValueElement = discountContainer.querySelector('.dl-bf-sales-products-m__discount-value');

            if (discountValueElement && discountValue && discountValue.trim() !== '') {
                discountValueElement.textContent = discountValue;
            }
        }

        updateCouponDisplay(item, colorOption) {
            const couponContainer = item.querySelector('.dl-bf-sales-products-m__item-coupon');
            if (!couponContainer) return;

            const couponCode = colorOption.dataset.dlVariantCouponCode;
            const couponPrefix = colorOption.dataset.dlCouponTextPrefix || '';
            const couponItem = couponContainer.querySelector('.dl-bf-sales-products-m__item-coupon-item');

            if (couponCode && couponCode.trim() !== '') {
                // Show coupon item if it exists, or create it if needed
                if (couponItem) {
                    couponItem.style.display = '';
                    const codeElement = couponItem.querySelector('.dl-bf-sales-products-m__item-coupon-item-code');
                    const copyButton = couponItem.querySelector('.dl-bf-sales-products-m__item-coupon-copy-btn');
                    
                    if (codeElement) {
                        // Update the code text with prefix from data attribute
                        codeElement.textContent = couponPrefix ? `${couponPrefix} ${couponCode}` : couponCode;
                    }
                    
                    if (copyButton) {
                        copyButton.dataset.discountCode = couponCode;
                    }
                }
            } else {
                // Hide coupon item if no coupon code for this variant
                if (couponItem) {
                    couponItem.style.display = 'none';
                }
            }
        }

        initReadMore() {
            this.readMoreContainer = this.querySelector('.dl-bf-sales-products-m__read-more');
            this.readMoreText = this.querySelector('.dl-bf-sales-products-m__read-more-text');
            this.hideText = this.querySelector('.dl-bf-sales-products-m__hide-text');
            this.isExpanded = false;

            // Get default display count for mobile and desktop
            this.defaultShowCount = this.readMoreContainer 
                ? parseInt(this.readMoreContainer.dataset.defaultShow || '4', 10) 
                : 4;
            this.defaultShowCountDesktop = this.readMoreContainer 
                ?  parseInt(this.readMoreContainer.dataset.defaultShowDesktop || '3', 10) 
                : 3;

            if (this.readMoreContainer) {
                // Bind Read More container click event
                const readMoreHandler = this.handleReadMore.bind(this);
                this.readMoreContainer.addEventListener('click', readMoreHandler);
                this.eventHandlers.set(this.readMoreContainer, ['click', readMoreHandler]);

                // Check initial visibility of hidden products
                this.checkReadMoreButtonVisibility();
            }
        }

        // Check if current device is desktop
        isDesktop() {
            return window.innerWidth > 990;
        }

        // Get default display count for current device
        getCurrentDefaultShowCount() {
            return this.isDesktop() ? this.defaultShowCountDesktop : this.defaultShowCount;
        }
        handleCopyCoupon(e) {
            e.stopPropagation();
            e.preventDefault();
            const discountCode = e.currentTarget.dataset.discountCode;
            if (discountCode) {
                copyTextToClipboard(discountCode);
                e.currentTarget.classList.add('dl-copied');
            }
        }
        initMainProduct() {
            // Find all main product items (support multiple main products)
            this.mainProductItems = this.querySelectorAll('.dl-bf-sales-products-m__item--main_product');
            // Initialize each main product separately
            this.mainProductItems.forEach((mainProductItem) => {
                const imageContainer = mainProductItem.querySelector('.dl-bf-sales-products-m__item-image--main_product');
                const colorsContainer = mainProductItem.querySelector('.dl-bf-sales-products-m__colors');
                const colors = mainProductItem.querySelectorAll('.dl-product-grid-item-h-colors-item');
                const copyCouponBtn = mainProductItem.querySelector('.dl-bf-sales-products-m__item-coupon-copy-btn');
                // Check if custom images are used (if yes, don't switch images)
                const noImageSwitch = colorsContainer && colorsContainer.dataset.dlNoImageSwitch === 'true';
                if (copyCouponBtn) {
                    copyCouponBtn.addEventListener('click', this.handleCopyCoupon.bind(this));
                    this.eventHandlers.set(copyCouponBtn, ['click', this.handleCopyCoupon.bind(this)]);
                }
                // Store references on the item for event handlers
                mainProductItem._imageContainer = imageContainer;
                mainProductItem._noImageSwitch = noImageSwitch;
                
                // Bind events for this main product
                if (colors && colors.length > 0) {
                    colors.forEach(color => {
                        const handler = (e) => this.handleColorClick(e, mainProductItem);
                        color.addEventListener('click', handler);
                        this.eventHandlers.set(color, ['click', handler]);
                    });
                }
            });

            // Also bind color switching events for other products in the list
            this.accessoryItems = this.querySelectorAll('.dl-bf-sales-products-m__item');
            if (this.accessoryItems.length > 0) {
                this.bindAccessoriesEvents();
            }
        }

        initAccessories() {
            this.accessoryItems = this.querySelectorAll('.dl-bf-sales-products-m__item');
            this.bindAccessoriesEvents();
        }

        checkReadMoreButtonVisibility() {
            const hiddenItems = this.getHiddenItems();

            if (this.readMoreContainer) {
                // Hide container if no hidden items and not expanded
                if (hiddenItems.length === 0 && !this.isExpanded) {
                    this.readMoreContainer.style.display = 'none';
                } else {
                    this.readMoreContainer.style.display = 'flex';
                }
            }
        }

        getProductItems() {
            // Get product items based on type
            return this.querySelectorAll('.dl-bf-sales-products-m__item');
        }

        getHiddenItems() {
            // Get hidden product items based on type and current device
            const selector = this.isDesktop() 
                ? '.dl-bf-sales-products-m__item.dl-hidden, .dl-bf-sales-products-m__item.dl-hidden-desktop'
                : '.dl-bf-sales-products-m__item.dl-hidden, .dl-bf-sales-products-m__item.dl-hidden-mobile';
            return this.querySelectorAll(selector);
        }


        bindAccessoriesEvents() {
            // Bind color selector events for all accessory products
            this.bindAccessoryColorEvents();
        }

        bindAccessoryColorEvents() {
            // Bind color selector events for each accessory product
            const items = this.querySelectorAll('.dl-bf-sales-products-m__item');

            items.forEach((item) => {
                // Check if event has already been bound
                if (item.dataset.colorBound === 'true') {
                    return;
                }

                const colors = item.querySelectorAll('.dl-product-grid-item-h-colors-item');
                const imageContainer = item.querySelector('.dl-bf-sales-products-m__item-image');

                if (colors && colors.length > 0) {
                    colors.forEach(color => {
                        const handler = (e) => this.handleAccessoryColorClick(e, item, imageContainer);
                        color.addEventListener('click', handler);
                        this.eventHandlers.set(color, ['click', handler]);
                    });

                    // Mark as bound
                    item.dataset.colorBound = 'true';
                }
            });
        }

        handleReadMore() {
            const hiddenItems = this.getHiddenItems();

            if (!this.isExpanded && hiddenItems.length > 0) {
                // Expand: Show more items based on device type
                // Desktop shows 3, mobile shows 4
                const showCount = this.getCurrentDefaultShowCount();
                const itemsToShow = Math.min(showCount, hiddenItems.length);
                
                for (let i = 0; i < itemsToShow; i++) {
                    // Remove all hidden classes
                    hiddenItems[i].classList.remove('dl-hidden', 'dl-hidden-desktop', 'dl-hidden-mobile');
                }

                // Bind color switching events for newly displayed products
                if (this.type === 'accessories') {
                    this.bindAccessoryColorEvents();
                }

                // Check if all items are now visible
                const remainingHidden = this.getHiddenItems();
                if (remainingHidden.length === 0) {
                    // All items visible, switch to Hide state
                    this.isExpanded = true;
                    this.toggleReadMoreText();
                }
            } else if (this.isExpanded) {
                // Collapse: Hide items based on current device type and component type
                const allItems = this.getProductItems();
                const currentDefaultCount = this.type === 'main_product_group' ? 0 : this.getCurrentDefaultShowCount();
                const isDesktop = this.isDesktop();
                allItems.forEach((item, index) => {
                    if (index >= currentDefaultCount) {
                        // Add appropriate hidden class based on device type
                        if (this.type === 'main_product') {
                            if (isDesktop && index >= this.defaultShowCountDesktop) {
                                item.classList.add('dl-hidden-desktop');
                            }
                            if (!isDesktop && index >= this.defaultShowCount) {
                                item.classList.add('dl-hidden-mobile');
                            }
                        } else {
                            // accessories type uses generic dl-hidden class
                            item.classList.add('dl-hidden');
                        }
                    }
                });

                // Switch back to Read More state
                this.isExpanded = false;
                this.toggleReadMoreText();

                // Scroll to top of list
                this.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }

        toggleReadMoreText() {
            if (!this.readMoreText || !this.hideText) return;

            if (this.isExpanded) {
                // Show Hide text
                this.readMoreText.style.display = 'none';
                this.hideText.style.display = 'inline';
                this.readMoreContainer?.classList.add('expanded');
            } else {
                // Show Read More text
                this.readMoreText.style.display = 'inline';
                this.hideText.style.display = 'none';
                this.readMoreContainer?.classList.remove('expanded');
            }
        }

        handleAccessoryColorClick(e, item, imageContainer) {
            e.stopPropagation();
            e.preventDefault();

            const colorOption = e.currentTarget;
            const { 
                dlVariantId: variantId, 
                dlVariantAvailable, 
                dlVariantSku: variantSku 
            } = colorOption.dataset;
            const variantAvailable = dlVariantAvailable === 'true';

            if (!variantId) {
                console.warn('handleAccessoryColorClick: variantId is missing');
                return;
            }

            // Update color selector state
            const colors = item.querySelectorAll('.dl-product-grid-item-h-colors-item');
            colors.forEach(color => color.classList.remove('dl-selected'));
            colorOption.classList.add('dl-selected');

            // Update variant ID in form
            this.updateVariantForm(item, variantId, variantSku, variantAvailable);

            // Update price display using pre-formatted prices from Liquid
            this.updatePriceDisplay(item, colorOption);

            // Update coupon code display
            this.updateCouponDisplay(item, colorOption);

            // Update discount display
            this.updateDiscountDisplay(item, colorOption);

            // Switch image
            this.switchProductImage(imageContainer, variantId);
        }

        updateVariantForm(item, variantId, variantSku, variantAvailable) {
            const formContainer = item.querySelector(`[id^="dl-bf-sales-products-form-${item.dataset.productId}"]`);
            if (!formContainer) return;

            const variantInput = formContainer.querySelector('.product-variant-id');
            if (!variantInput) return;

            variantInput.value = variantId;
            variantInput.dataset.variantSku = variantSku;

            // Update button disabled state and text
            const submitButton = formContainer.querySelector('.dl-product-form__submit-button-h');
            if (submitButton) {
                submitButton.disabled = !variantAvailable;
            }
            // Update quantity input disabled state
            const quantityInput = formContainer.querySelector('.product-variant-quantity');
            if (quantityInput) {
                quantityInput.disabled = !variantAvailable;
            }
        }

        switchProductImage(imageContainer, variantId) {
            if (!imageContainer) return;

            const images = imageContainer.querySelectorAll('img');
            const variantImages = imageContainer.querySelectorAll(`img[data-dl-variant-id="${variantId}"]`);

            if (variantImages.length > 0) {
                imageContainer.classList.add('dl-change');
                images.forEach(image => image.classList.remove('dl-selected'));
                variantImages.forEach(image => image.classList.add('dl-selected'));

                setTimeout(() => {
                    imageContainer.classList.remove('dl-change');
                }, 300);
            }
        }

        async handleColorClick(e, mainProductItem) {
            e.stopPropagation();
            e.preventDefault();

            const colorOption = e.currentTarget;
            const { 
                dlVariantId: variantId, 
                dlVariantAvailable, 
                dlVariantSku: variantSku 
            } = colorOption.dataset;
            const variantAvailable = dlVariantAvailable === 'true';

            // Validate data
            if (!variantId) {
                console.warn('handleColorClick: variantId is missing');
                return;
            }

            // If mainProductItem is not provided, find it from the color option
            if (!mainProductItem) {
                mainProductItem = colorOption.closest('.dl-bf-sales-products-m__item--main_product');
            }

            // Update color selector state for this main product only
            const colors = mainProductItem.querySelectorAll('.dl-product-grid-item-h-colors-item');
            colors.forEach(color => color.classList.remove('dl-selected'));
            colorOption.classList.add('dl-selected');

            // Update variant ID in form
            this.updateVariantForm(mainProductItem, variantId, variantSku, variantAvailable);

            // Update price display using pre-formatted prices from Liquid
            this.updatePriceDisplay(mainProductItem, colorOption);

            // Update coupon code display
            this.updateCouponDisplay(mainProductItem, colorOption);

            // Update discount display
            this.updateDiscountDisplay(mainProductItem, colorOption);

            // Get image container and noImageSwitch flag for this main product
            const imageContainer = mainProductItem._imageContainer;
            const noImageSwitch = mainProductItem._noImageSwitch;

            // Don't switch image if custom images are used
            if (noImageSwitch) {
                return;
            }

            // Switch image if no custom images
            this.switchProductImage(imageContainer, variantId);
        }
    }

    customElements.define('dl-bf-sales-products-m', DlBfSalesProductsM);
}

